import { type ApolloCache, useApolloClient, useMutation } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import useNotify from '../../hooks/useNotify';
import {
  ACTIVITY_ITEM_FIELDS,
  DISMISS_NOTIFICATION,
  GET_ACTIVITY_FEED,
  GET_ACTIVITY_ITEM_IDS,
  GET_ACTIVITY_ITEMS,
  MARK_AS_FOLLOW_UP,
  MARK_NOTIFICATION_READ,
  MARK_NOTIFICATION_UNREAD,
  UNMARK_AS_FOLLOW_UP,
  UNDISMISS_NOTIFICATION,
  type ActivityFeedItem,
} from './ActivityFeed.queries';

type ActivityItemOptimisticPatch = {
  read?: boolean;
  dismissed?: boolean;
  followUp?: number | null;
};

const invalidateActivityQueries = (cache: ApolloCache<unknown>) => {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'activityFeed' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'activityItems' });
};

export default function useNotificationActions() {
  const { showError } = useNotify();
  const client = useApolloClient();

  const refetchActivityQueries = useCallback(async () => {
    await client.refetchQueries({
      include: [GET_ACTIVITY_FEED, GET_ACTIVITY_ITEM_IDS, GET_ACTIVITY_ITEMS],
      updateCache: invalidateActivityQueries,
    });
  }, [client]);

  const getOptimisticActivityItem = useCallback(
    (
      notificationId: string | number,
      patch: ActivityItemOptimisticPatch
    ): ActivityFeedItem | undefined => {
      const id = client.cache.identify({
        __typename: 'ActivityItem',
        id: String(notificationId),
      });
      const activityItem = id
        ? client.cache.readFragment({
            id,
            fragment: ACTIVITY_ITEM_FIELDS,
          })
        : null;

      if (!activityItem) {
        return undefined;
      }

      return {
        ...activityItem,
        ...patch,
      };
    },
    [client.cache]
  );

  const [markNotificationRead, { loading: markReadLoading }] = useMutation(
    MARK_NOTIFICATION_READ,
    {
      onError: (error) => {
        showError('Could not mark notification as read.', error);
      },
    }
  );

  const [markNotificationUnread, { loading: markUnreadLoading }] = useMutation(
    MARK_NOTIFICATION_UNREAD,
    {
      onError: (error) => {
        showError('Could not mark notification as unread.', error);
      },
    }
  );

  const [markAsFollowUpMutation, { loading: markAsFollowUpLoading }] =
    useMutation(MARK_AS_FOLLOW_UP, {
      onError: (error) => {
        showError('Could not mark notification as follow-up.', error);
      },
    });

  const [unMarkAsFollowUpMutation, { loading: unMarkAsFollowUpLoading }] =
    useMutation(UNMARK_AS_FOLLOW_UP, {
      onError: (error) => {
        showError('Could not unmark notification as follow-up.', error);
      },
    });

  const [dismissNotification, { loading: dismissLoading }] = useMutation(
    DISMISS_NOTIFICATION,
    {
      onError: (error) => {
        showError('Could not dismiss notification.', error);
      },
    }
  );

  const [undismissNotification, { loading: undismissLoading }] = useMutation(
    UNDISMISS_NOTIFICATION,
    {
      onError: (error) => {
        showError('Could not un-dismiss notification.', error);
      },
    }
  );

  const markRead = useCallback(
    async (notificationId: string | number) => {
      const optimisticActivityItem = getOptimisticActivityItem(notificationId, {
        read: true,
      });

      await markNotificationRead({
        variables: { notificationId: String(notificationId) },
        optimisticResponse: optimisticActivityItem
          ? { markNotificationRead: optimisticActivityItem }
          : undefined,
      });
      await refetchActivityQueries();
    },
    [getOptimisticActivityItem, markNotificationRead, refetchActivityQueries]
  );

  const markUnread = useCallback(
    async (notificationId: string | number) => {
      const optimisticActivityItem = getOptimisticActivityItem(notificationId, {
        read: false,
      });

      await markNotificationUnread({
        variables: { notificationId: String(notificationId) },
        optimisticResponse: optimisticActivityItem
          ? { markNotificationUnread: optimisticActivityItem }
          : undefined,
      });
      await refetchActivityQueries();
    },
    [getOptimisticActivityItem, markNotificationUnread, refetchActivityQueries]
  );

  const dismiss = useCallback(
    async (notificationId: string | number) => {
      const optimisticActivityItem = getOptimisticActivityItem(notificationId, {
        dismissed: true,
        followUp: null,
      });

      await dismissNotification({
        variables: { notificationId: String(notificationId) },
        optimisticResponse: optimisticActivityItem
          ? { dismissNotification: optimisticActivityItem }
          : undefined,
      });
      await refetchActivityQueries();
    },
    [dismissNotification, getOptimisticActivityItem, refetchActivityQueries]
  );

  const markAsFollowUp = useCallback(
    async (notificationId: string | number) => {
      const optimisticActivityItem = getOptimisticActivityItem(notificationId, {
        dismissed: false,
        followUp: Date.now(),
      });

      await markAsFollowUpMutation({
        variables: { notificationId: String(notificationId) },
        optimisticResponse: optimisticActivityItem
          ? { markAsFollowUp: optimisticActivityItem }
          : undefined,
      });
      await refetchActivityQueries();
    },
    [getOptimisticActivityItem, markAsFollowUpMutation, refetchActivityQueries]
  );

  const unMarkAsFollowUp = useCallback(
    async (notificationId: string | number) => {
      const optimisticActivityItem = getOptimisticActivityItem(notificationId, {
        followUp: null,
      });

      await unMarkAsFollowUpMutation({
        variables: { notificationId: String(notificationId) },
        optimisticResponse: optimisticActivityItem
          ? { unMarkAsFollowUp: optimisticActivityItem }
          : undefined,
      });
      await refetchActivityQueries();
    },
    [
      getOptimisticActivityItem,
      refetchActivityQueries,
      unMarkAsFollowUpMutation,
    ]
  );

  const undismiss = useCallback(
    async (notificationId: string | number) => {
      const optimisticActivityItem = getOptimisticActivityItem(notificationId, {
        dismissed: false,
      });

      await undismissNotification({
        variables: { notificationId: String(notificationId) },
        optimisticResponse: optimisticActivityItem
          ? { undismissNotification: optimisticActivityItem }
          : undefined,
      });
      await refetchActivityQueries();
    },
    [getOptimisticActivityItem, refetchActivityQueries, undismissNotification]
  );

  return useMemo(
    () => ({
      markRead,
      markUnread,
      markAsFollowUp,
      unMarkAsFollowUp,
      dismiss,
      undismiss,
      loading:
        markReadLoading ||
        markUnreadLoading ||
        markAsFollowUpLoading ||
        unMarkAsFollowUpLoading ||
        dismissLoading ||
        undismissLoading,
    }),
    [
      dismiss,
      dismissLoading,
      markAsFollowUp,
      markAsFollowUpLoading,
      markRead,
      markReadLoading,
      markUnread,
      markUnreadLoading,
      unMarkAsFollowUp,
      unMarkAsFollowUpLoading,
      undismiss,
      undismissLoading,
    ]
  );
}

export type NotificationActions = ReturnType<typeof useNotificationActions>;
